import { useSelector } from "react-redux";
import styled from "styled-components";

import { customerSelector } from "@/react/features/customer/customerSlice";
import {
  useCreatePbxesMutation,
  useDeletePbxMutation,
  useGetPbxesQuery,
} from "./pbxApi";
import { useTable } from "@/react/components/table/useTable";
import schema from "./schema";
import columnDefinition from "./columnDefinition";
import TableComponent from "@/react/components/table/TableComponent";
import React, { useCallback, useMemo, useState } from "react";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { CustomerCard } from "@/react/features/customer/card";
import { EMPTY_ARRAY } from "@/react/empty";
import Tabs, { Tab } from "@8x8/oxygen-tabs";
import mergeNewRowIntoSelection from "@/react/components/table/mergeNewRowIntoSelection";
import JobsByTypePage from "@/react/features/jobs/JobsByTypePage";
import { useRetrieveAddressesByCustomerQuery } from "@/react/services/common/addressApi";
import { notify, TYPE } from "@8x8/oxygen-toaster";
import omit from "lodash/omit";
import PageLayout from "@/react/features/styled/PageLayout";
import TableWrapper from "@/react/features/styled/TableWrapper";
import Typography from "@8x8/oxygen-typography";

const columns = Object.freeze(columnDefinition());

const TABLES = Object.freeze({
  PBX: "PBX",
  JOBS: "Jobs",
});

const findAddressById = (addresses, addressId) => {
  if (!addressId) {
    return null;
  }

  return addresses[addressId] || null;
};

const StyledTabs = styled(Tabs)`
  margin: 0 0 16px;
`;

export const PbxPage = () => {
  const customer = useSelector(customerSelector);
  const [tableDisplayed, setTableDisplayed] = useState(TABLES.PBX);

  const { data: addresses } = useRetrieveAddressesByCustomerQuery();

  const { data: initialData, isFetching, error, refetch } = useGetPbxesQuery();

  const [createPbx, { isLoading: isCreating }] = useCreatePbxesMutation();

  const [{ isLoading: isDeleting }] = useDeletePbxMutation();

  useErrorNotification(error);

  const idKey = schema.fields.pbxId.spec.key;

  const { table, filters, state, mutations } = useTable(
    initialData ?? EMPTY_ARRAY,
    columns,
    schema,
    idKey,
    {
      enableRowSelection: (row) => {
        return !row.original[idKey];
      },
    }
  );

  const { setRowSelection } = state;

  const { rowsToCreate, resetData, addRows } = mutations;

  const newRowSelection = useCallback(
    // This is the same as (rowsToAdd, schema) => mergeNewRowIntoSelection({addRows, setRowSelection})(rowsToAdd, schema) - since the function returns a callback
    mergeNewRowIntoSelection({ addRows, setRowSelection }),
    [addRows, setRowSelection]
  );

  const onSave = useCallback(async () => {
    if (rowsToCreate.size > 0) {
      /**
       * Checking all the names before proceeding to Job creation.
       * If one PBX name collision check fails, the create operation is aborted and all the rows that contain collisions are marked with errors.
       */

      createPbx({
        pbxes: Array.from(rowsToCreate.values()).map((pbx) => ({
          ...pbx,
          details: {
            ...pbx.details,
            emergencyAddress: findAddressById(
              addresses,
              pbx.details.emergencyAddressId
            ),
          },
          customerId: customer?.cid,
        })),
      })
        .unwrap()
        .then((response) => {
          // Since the response is UUID of job created, handleMutationResponse will not work as expected.
          notify({
            content: `Job ${response} created.`,
            type: TYPE.success,
            duration: 5000,
          });
        })
        .catch(errorNotification);
    }
  }, [rowsToCreate]);

  const finalMutations = useMemo(
    () => ({
      ...omit(mutations, ["deleteRows", "updateRows"]),
      addRows: newRowSelection,
    }),
    [mutations]
  );

  const isDirty = rowsToCreate.size > 0;

  const isLoading = isFetching || isCreating || isDeleting;

  return (
    <PageLayout>
      <CustomerCard />
      {!customer && <div>Select a customer</div>}
      <StyledTabs color="light">
        {Object.values(TABLES).map((tableName) => (
          <Tab
            key={tableName}
            color="light"
            onClick={() => setTableDisplayed(tableName)}
            isActive={tableDisplayed === tableName}
          >
            <Typography color={"inherit"} size={"md"}>
              {tableName}
            </Typography>
          </Tab>
        ))}
      </StyledTabs>
      <TableWrapper>
        {tableDisplayed === TABLES.PBX ? (
          <TableComponent
            title="PBX"
            schema={schema}
            isDirty={isDirty}
            isLoading={isLoading}
            onSave={onSave}
            onCancel={resetData}
            onRefresh={refetch}
            table={table}
            filters={filters}
            mutations={finalMutations}
            state={state}
          />
        ) : (
          <JobsByTypePage type={"PBX_CREATE"} />
        )}
      </TableWrapper>
    </PageLayout>
  );
};
